import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Trophy, Crown, Medal, Coins, Gem, Star, Play, Gift, Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "fruitCrush_token";

function api(path, opts = {}) {
  const token = localStorage.getItem(TOKEN_KEY);
  return fetch(`${API_URL}/api${path}`, {
    ...opts,
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json", ...opts.headers },
  }).then((r) => r.json()).catch(() => ({}));
}

function rankBadge(rank) {
  if (rank === 1) return { icon: <Crown className="w-4 h-4" />, color: "text-amber-300", bg: "bg-amber-500/20 border-amber-400/40" };
  if (rank <= 3) return { icon: <Trophy className="w-4 h-4" />, color: "text-orange-300", bg: "bg-orange-500/15 border-orange-400/30" };
  if (rank <= 10) return { icon: <Medal className="w-4 h-4" />, color: "text-slate-200", bg: "bg-slate-500/15 border-slate-400/30" };
  if (rank <= 100) return { icon: <Star className="w-4 h-4" />, color: "text-cyan-300", bg: "bg-cyan-500/10 border-cyan-400/30" };
  return { icon: null, color: "text-slate-400", bg: "bg-slate-800/40 border-white/5" };
}

function RewardTier({ tier, myRank }) {
  const reached = myRank && myRank >= tier.min && myRank <= tier.max;
  return (
    <div
      className={`flex items-center justify-between px-3 py-2 rounded-lg border ${
        reached ? "bg-amber-500/10 border-amber-400/40" : "bg-slate-800/30 border-white/5"
      }`}
      data-testid={`reward-tier-${tier.label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="flex items-center gap-2">
        <span className={`text-xs font-bold ${reached ? "text-amber-300" : "text-slate-300"}`}>
          {tier.label}
        </span>
        <span className="text-[10px] text-slate-500">
          {tier.min === tier.max ? `#${tier.min}` : `#${tier.min}-${tier.max}`}
        </span>
      </div>
      <div className="flex items-center gap-2 text-xs">
        {tier.coins > 0 && (
          <span className="flex items-center gap-1 text-amber-300">
            <Coins className="w-3 h-3" />{tier.coins}
          </span>
        )}
        {tier.gems > 0 && (
          <span className="flex items-center gap-1 text-cyan-300">
            <Gem className="w-3 h-3" />{tier.gems}
          </span>
        )}
      </div>
    </div>
  );
}

function LeaderboardRow({ entry }) {
  const badge = rankBadge(entry.rank);
  const isMe = entry.is_me;

  return (
    <div
      className={`flex items-center gap-3 px-3 py-2 rounded-lg border ${badge.bg} ${
        isMe ? "ring-2 ring-amber-400/60 shadow-lg shadow-amber-500/10" : ""
      }`}
      data-testid={`lb-row-rank-${entry.rank}`}
    >
      <div className={`w-8 text-center font-bold text-sm ${badge.color}`}>
        {entry.rank <= 3 ? badge.icon : `#${entry.rank}`}
      </div>
      <div className="text-2xl">{entry.avatar}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={`text-sm font-semibold truncate ${isMe ? "text-amber-300" : "text-white"}`}>
            {entry.name}
          </span>
          {isMe && (
            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-500/30 text-amber-200 font-bold">
              YOU
            </span>
          )}
          {entry.is_bot && (
            <span className="text-[9px] text-slate-500">bot</span>
          )}
        </div>
        <div className="text-[10px] text-slate-500">
          {entry.score.toLocaleString()} pts
        </div>
      </div>
    </div>
  );
}

export default function TournamentPage() {
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [myRank, setMyRank] = useState(null);
  const [myScore, setMyScore] = useState(0);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [infoRes, lbRes, progRes] = await Promise.all([
      api("/tournament/info"),
      api("/tournament/leaderboard"),
      api("/tournament/my-progress"),
    ]);
    if (infoRes && infoRes.tournament_levels) setInfo(infoRes);
    if (lbRes && lbRes.leaderboard) {
      setLeaderboard(lbRes.leaderboard);
      setMyRank(lbRes.my_rank);
      setMyScore(lbRes.my_score);
    }
    if (progRes && progRes.tournament_levels) setProgress(progRes);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const onClaim = async () => {
    setClaiming(true);
    const res = await api("/tournament/claim-rewards", { method: "POST" });
    setClaiming(false);
    if (res.detail) {
      toast.error(res.detail);
      return;
    }
    if (res.rewards) {
      const r = res.rewards;
      const parts = [];
      if (r.coins) parts.push(`${r.coins} coins`);
      if (r.gems) parts.push(`${r.gems} gems`);
      toast.success(`Claimed ${res.tier}! +${parts.join(" & ")}`);
      load();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-amber-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-amber-950/20 to-slate-950 pb-20">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-slate-950/90 backdrop-blur-md border-b border-white/5">
        <div className="max-w-lg mx-auto flex items-center gap-3 px-4 py-3">
          <button
            onClick={() => navigate("/levels")}
            className="p-2 rounded-lg hover:bg-white/5 active:scale-95 transition-all"
            data-testid="tournament-back-btn"
          >
            <ArrowLeft className="w-5 h-5 text-slate-300" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold text-white flex items-center gap-2">
              <Trophy className="w-5 h-5 text-amber-400" />
              Tournament
            </h1>
            <p className="text-[10px] text-slate-500">Always-active continuous ranking</p>
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-4 space-y-4">
        {/* My Status */}
        <Card
          className="bg-gradient-to-br from-amber-500/15 to-orange-600/10 border-amber-400/30 p-4"
          data-testid="my-tournament-status"
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-wide text-amber-300/80">Your rank</div>
              <div className="text-3xl font-black text-white">
                {myRank ? `#${myRank}` : "—"}
              </div>
              <div className="text-xs text-slate-300 mt-1">
                {myScore.toLocaleString()} pts total
              </div>
            </div>
            <Button
              onClick={onClaim}
              disabled={claiming || !myRank || myRank > 100}
              className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold disabled:opacity-50"
              data-testid="claim-rewards-btn"
            >
              {claiming ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Gift className="w-4 h-4 mr-1.5" />
                  Claim
                </>
              )}
            </Button>
          </div>
          {myRank && myRank > 100 && (
            <p className="text-[10px] text-amber-300/70 mt-2">
              Reach top 100 to claim rewards!
            </p>
          )}
        </Card>

        {/* How it works */}
        {info && (
          <Card className="bg-slate-800/40 border-white/5 p-4">
            <h2 className="text-sm font-bold text-white mb-2">How it works</h2>
            <p className="text-xs text-slate-400 mb-3">{info.description}</p>

            {/* Tournament levels */}
            <div className="mb-3">
              <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1.5">
                Tournament Levels
              </div>
              <div className="grid grid-cols-5 gap-2">
                {info.tournament_levels.map((lvl) => {
                  const score = progress?.level_scores?.[String(lvl)] || 0;
                  return (
                    <button
                      key={lvl}
                      onClick={() => navigate(`/play/${lvl}`)}
                      className="bg-slate-900/60 border border-amber-400/20 hover:border-amber-400/60 hover:bg-amber-500/10 rounded-lg p-2 transition-all active:scale-95 text-center"
                      data-testid={`tournament-level-${lvl}`}
                    >
                      <div className="text-[10px] text-slate-500">Lv</div>
                      <div className="text-base font-bold text-amber-300">{lvl}</div>
                      <div className="text-[9px] text-slate-500 mt-0.5 truncate">
                        {score > 0 ? score.toLocaleString() : "—"}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Rewards */}
            <div>
              <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1.5">
                Reward Tiers (claim every 24h)
              </div>
              <div className="space-y-1.5">
                {info.rewards.map((t) => (
                  <RewardTier key={t.label} tier={t} myRank={myRank} />
                ))}
              </div>
            </div>
          </Card>
        )}

        {/* Quick play CTA */}
        {info && (
          <Button
            onClick={() => navigate(`/play/${info.tournament_levels[0]}`)}
            className="w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-400 hover:to-orange-500 text-slate-900 font-bold h-12"
            data-testid="quick-play-tournament-btn"
          >
            <Play className="w-4 h-4 mr-2 fill-current" />
            Play Tournament Level
          </Button>
        )}

        {/* Leaderboard */}
        <Card className="bg-slate-800/40 border-white/5 p-4" data-testid="tournament-leaderboard">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-bold text-white">Leaderboard</h2>
            <span className="text-[10px] text-slate-500">
              Top {Math.min(leaderboard.length, 100)}
            </span>
          </div>
          <div className="space-y-1.5">
            {leaderboard.length === 0 && (
              <div className="text-xs text-slate-500 text-center py-6">No entries yet</div>
            )}
            {leaderboard.map((entry) => (
              <LeaderboardRow key={`${entry.id}-${entry.rank}`} entry={entry} />
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
